import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { BarChart3, Users, DollarSign, ShoppingCart, Mail, MessageSquare, TrendingUp, Target } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalyticsDashboard({ onNavigate }) {
  const [crm, setCrm] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [segments, setSegments] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [crmRes, campRes, segRes] = await Promise.all([
        axios.get(`${API}/crm/analytics`, { withCredentials: true }),
        axios.get(`${API}/campaigns/analytics`, { withCredentials: true }),
        axios.get(`${API}/crm/segments`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setCrm(crmRes.data);
      setCampaigns(campRes.data);
      setSegments(segRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="text-zinc-500 text-center py-12">Loading analytics...</div>;

  const handleSourceClick = (source) => {
    if (onNavigate) onNavigate("leads", { filterSource: source });
  };
  const handlePipelineClick = (status) => {
    if (onNavigate) onNavigate("leads", { filterStatus: status });
  };
  const handleSegmentClick = (segId) => {
    if (onNavigate) onNavigate("leads", { filterSegment: segId });
  };

  const totalPipelineLeads = crm ? Object.values(crm.pipeline_value || {}).reduce((a, b) => a + b, 0) : 0;

  const PipelineBar = ({ label, value, total, color, statusKey }) => {
    const pct = total > 0 ? (value / total * 100) : 0;
    return (
      <button onClick={() => handlePipelineClick(statusKey)} className="flex items-center gap-3 text-sm w-full group hover:bg-zinc-800/50 rounded-lg px-1 py-0.5 transition">
        <span className="text-zinc-500 w-28 text-right truncate group-hover:text-orange-400 transition">{label}</span>
        <div className="flex-1 h-6 bg-zinc-800 rounded-full overflow-hidden">
          <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${Math.max(pct, 2)}%` }} />
        </div>
        <span className="text-zinc-400 w-12 text-right group-hover:text-white">{value}</span>
      </button>
    );
  };

  const SEGMENT_COLORS = { vip: "bg-amber-500/20 text-amber-400", regular: "bg-blue-500/20 text-blue-400", new: "bg-green-500/20 text-green-400", at_risk: "bg-red-500/20 text-red-400", high_value: "bg-purple-500/20 text-purple-400", dormant: "bg-zinc-600/20 text-zinc-400" };

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
        <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400" /> Analytics Dashboard
      </h2>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <button onClick={() => onNavigate?.("leads")} className="text-left">
          <Card className="bg-zinc-900 border-zinc-800 hover:border-orange-500/30 transition" data-testid="stat-total-leads">
            <CardContent className="p-4 sm:p-5">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-zinc-800 text-orange-400 mb-3"><Users className="w-5 h-5" /></div>
              <p className="text-xl sm:text-2xl font-bold text-white">{crm?.leads?.total || 0}</p>
              <p className="text-zinc-500 text-sm">Total Leads</p>
              <p className="text-xs text-zinc-600 mt-1">{crm?.leads?.conversion_rate || 0}% conversion</p>
            </CardContent>
          </Card>
        </button>
        <button onClick={() => onNavigate?.("leads", { tab: "clients" })} className="text-left">
          <Card className="bg-zinc-900 border-zinc-800 hover:border-blue-500/30 transition" data-testid="stat-clients">
            <CardContent className="p-4 sm:p-5">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-zinc-800 text-blue-400 mb-3"><Users className="w-5 h-5" /></div>
              <p className="text-xl sm:text-2xl font-bold text-white">{crm?.clients?.total || 0}</p>
              <p className="text-zinc-500 text-sm">Clients</p>
            </CardContent>
          </Card>
        </button>
        <button onClick={() => onNavigate?.("orders")} className="text-left">
          <Card className="bg-zinc-900 border-zinc-800 hover:border-green-500/30 transition" data-testid="stat-orders">
            <CardContent className="p-4 sm:p-5">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-zinc-800 text-green-400 mb-3"><ShoppingCart className="w-5 h-5" /></div>
              <p className="text-xl sm:text-2xl font-bold text-white">{crm?.orders?.total || 0}</p>
              <p className="text-zinc-500 text-sm">Orders</p>
            </CardContent>
          </Card>
        </button>
        <Card className="bg-zinc-900 border-zinc-800" data-testid="stat-revenue">
          <CardContent className="p-4 sm:p-5">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-zinc-800 text-emerald-400 mb-3"><DollarSign className="w-5 h-5" /></div>
            <p className="text-xl sm:text-2xl font-bold text-white">${(crm?.orders?.revenue || 0).toLocaleString()}</p>
            <p className="text-zinc-500 text-sm">Revenue</p>
            <p className="text-xs text-zinc-600 mt-1">{crm?.orders?.paid || 0} paid</p>
          </CardContent>
        </Card>
      </div>

      {/* Segments */}
      {segments && segments.segment_counts && Object.keys(segments.segment_counts).length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-orange-400" /> Customer Segments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {Object.entries(segments.definitions || {}).map(([id, def]) => (
                <button key={id} onClick={() => handleSegmentClick(id)} className="bg-zinc-800 rounded-lg p-3 hover:bg-zinc-700 transition text-left" data-testid={`segment-${id}`}>
                  <Badge className={`${SEGMENT_COLORS[id] || "bg-zinc-700 text-zinc-400"} text-xs mb-1`}>{def.label}</Badge>
                  <p className="text-lg font-bold text-white">{segments.segment_counts[id] || 0}</p>
                  <p className="text-zinc-600 text-[10px] leading-tight mt-0.5">{def.description}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Lead Pipeline */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-orange-400" /> Lead Pipeline
              <span className="text-zinc-600 text-xs font-normal ml-auto">Click to filter</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <PipelineBar label="New" value={crm?.pipeline_value?.new || 0} total={totalPipelineLeads} color="bg-blue-500" statusKey="new" />
            <PipelineBar label="Contacted" value={crm?.pipeline_value?.contacted || 0} total={totalPipelineLeads} color="bg-cyan-500" statusKey="contacted" />
            <PipelineBar label="Qualified" value={crm?.pipeline_value?.qualified || 0} total={totalPipelineLeads} color="bg-yellow-500" statusKey="qualified" />
            <PipelineBar label="Proposal" value={crm?.pipeline_value?.proposal_sent || 0} total={totalPipelineLeads} color="bg-orange-500" statusKey="proposal_sent" />
            <PipelineBar label="Negotiation" value={crm?.pipeline_value?.negotiation || 0} total={totalPipelineLeads} color="bg-purple-500" statusKey="negotiation" />
            <PipelineBar label="Won" value={crm?.pipeline_value?.won || 0} total={totalPipelineLeads} color="bg-green-500" statusKey="won" />
            <PipelineBar label="Lost" value={crm?.pipeline_value?.lost || 0} total={totalPipelineLeads} color="bg-red-500" statusKey="lost" />
          </CardContent>
        </Card>

        <div className="space-y-4">
          {/* Campaign Stats */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-orange-400" /> Campaigns
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => onNavigate?.("campaigns")} className="bg-zinc-800 rounded-lg p-4 text-center hover:bg-zinc-700 transition">
                  <p className="text-2xl font-bold text-white">{campaigns?.email?.campaigns || 0}</p>
                  <p className="text-zinc-500 text-xs">Email Campaigns</p>
                  <p className="text-orange-400 text-sm mt-1">{campaigns?.email?.total_sent || 0} sent</p>
                </button>
                <button onClick={() => onNavigate?.("campaigns")} className="bg-zinc-800 rounded-lg p-4 text-center hover:bg-zinc-700 transition">
                  <p className="text-2xl font-bold text-white">{campaigns?.sms?.campaigns || 0}</p>
                  <p className="text-zinc-500 text-xs">SMS Campaigns</p>
                  <p className="text-orange-400 text-sm mt-1">{campaigns?.sms?.total_sent || 0} sent</p>
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Cart Recovery */}
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-orange-400" /> Cart Recovery
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div><p className="text-zinc-500 text-sm">Abandoned</p><p className="text-xl font-bold text-white">{crm?.carts?.abandoned || 0}</p></div>
                <div><p className="text-zinc-500 text-sm">Recovered</p><p className="text-xl font-bold text-green-400">{crm?.carts?.recovered || 0}</p></div>
                <div><p className="text-zinc-500 text-sm">Rate</p><p className="text-xl font-bold text-orange-400">{crm?.carts?.recovery_rate || 0}%</p></div>
              </div>
            </CardContent>
          </Card>

          {/* Lead Sources */}
          {crm?.sources && Object.keys(crm.sources).length > 0 && (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white text-base sm:text-lg">Lead Sources <span className="text-zinc-600 text-xs font-normal ml-2">Click to filter</span></CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(crm.sources).map(([source, count]) => (
                    <button key={source} onClick={() => handleSourceClick(source)} className="group" data-testid={`source-${source}`}>
                      <Badge className="bg-zinc-800 text-zinc-300 px-3 py-1.5 group-hover:bg-orange-500/20 group-hover:text-orange-400 transition cursor-pointer">
                        {source.replace(/_/g, ' ')} <span className="ml-1 text-orange-400">{count}</span>
                      </Badge>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
